import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ApiError, useApi } from '@/api/client';
import { downloadBase64, fileToBase64 } from '@/lib/attachments';
import { fmtNumber, fmtUsd } from '@/lib/format';
import type { FileTaskInputFile, FileTaskResponse } from '@/types';

const maxFileBytes = 10 * 1024 * 1024;
const maxTotalBytes = 25 * 1024 * 1024;

function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.detail ?? e.message;
  return e instanceof Error ? e.message : 'Something went wrong.';
}

export function FileTasks() {
  const api = useApi();
  const models = useQuery({ queryKey: ['models'], queryFn: api.listModels });
  // Only models flagged for the code-execution sandbox can run file tasks.
  const fileTaskModels = useMemo(
    () => (models.data ?? []).filter(m => m.supportsFileTasks),
    [models.data]);

  const [files, setFiles] = useState<FileTaskInputFile[]>([]);
  const [instructions, setInstructions] = useState('');
  const [modelId, setModelId] = useState('');
  const [fileError, setFileError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const addFiles = async (list: FileList | null) => {
    if (!list) return;
    setFileError(null);
    const additions: FileTaskInputFile[] = [];
    for (const file of [...list]) {
      if (file.size > maxFileBytes) {
        setFileError(`'${file.name}' is ${(file.size / 1024 / 1024).toFixed(1)} MB; the limit is 10 MB per file.`);
        continue;
      }
      additions.push({
        fileName: file.name,
        mediaType: file.type || undefined,
        data: await fileToBase64(file)
      });
    }
    setFiles(prev => {
      const merged = [...prev, ...additions];
      const total = merged.reduce((sum, f) => sum + f.data.length * 0.75, 0);
      if (total > maxTotalBytes) {
        setFileError('Files exceed the 25 MB per-request limit.');
        return prev;
      }
      return merged.slice(0, 10);
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const run = useMutation<FileTaskResponse, unknown>({
    mutationFn: () => api.fileTask({
      instructions,
      files,
      modelId: modelId || undefined
    })
  });

  const canRun = files.length > 0 && instructions.trim().length > 0 && !run.isPending;
  const result = run.data;

  return (
    <>
      <div className="page-header">
        <div>
          <h1>File tasks</h1>
          <div className="sub">
            Send files with instructions; Claude writes and runs code against them in a sandbox
            and returns the files it produces — e.g. "merge these spreadsheets and add a totals column".
          </div>
        </div>
      </div>

      <div className="grid-2" style={{ alignItems: 'start' }}>
        <div className="card">
          <div className="card-header"><h3>Task</h3></div>
          <div className="card-body">
            <div className="field">
              <label>Files (xlsx, csv, pdf, images, ...)</label>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={e => addFiles(e.target.files)}
              />
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <button type="button" className="btn-secondary btn-sm" onClick={() => fileInputRef.current?.click()}>
                  Add files
                </button>
                {files.map((f, i) => (
                  <span key={`${f.fileName}-${i}`} className="pill pill-disabled" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                    📄 {f.fileName}
                    <button
                      type="button"
                      aria-label={`Remove ${f.fileName}`}
                      onClick={() => setFiles(prev => prev.filter((_, idx) => idx !== i))}
                      style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0, lineHeight: 1 }}>
                      ✕
                    </button>
                  </span>
                ))}
              </div>
              {fileError && <div style={{ color: 'var(--kt-red)', fontSize: '.8rem', marginTop: 6 }}>{fileError}</div>}
            </div>

            <div className="field">
              <label htmlFor="instructions">Instructions</label>
              <textarea
                id="instructions"
                rows={6}
                placeholder="e.g. Combine these two spreadsheets on the Booking Ref column, add a Margin column (Sell - Cost), and output one xlsx with a summary sheet."
                value={instructions}
                onChange={e => setInstructions(e.target.value)}
              />
            </div>

            <div className="field">
              <label htmlFor="ft-model">Model</label>
              <select id="ft-model" className="select" style={{ width: '100%' }} value={modelId} onChange={e => setModelId(e.target.value)}>
                <option value="">Gateway default — Claude Sonnet 5</option>
                {fileTaskModels.map(m => (
                  <option key={m.modelId} value={m.modelId}>
                    {m.displayName} — {fmtUsd(m.inputCostPerMillionUsd)} in / {fmtUsd(m.outputCostPerMillionUsd)} out per 1M
                  </option>
                ))}
              </select>
              <div className="hint">
                Only models that accept file tasks appear here — the code-execution sandbox is
                Anthropic-only today.
              </div>
            </div>

            <button className="btn-primary" onClick={() => run.mutate()} disabled={!canRun}>
              {run.isPending ? 'Working... (runs real code, can take a few minutes)' : 'Run task'}
            </button>
            <div style={{ fontSize: '.78rem', color: 'var(--kt-grey-400)', marginTop: 8 }}>
              File content is not PII-scanned and goes to Anthropic's sandbox. Tokens are billed
              against your budget as usual.
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3>Result</h3>
            {result && <span className="row-sub" style={{ marginTop: 0 }}>{result.correlationId}</span>}
          </div>
          <div className="card-body">
            {run.isError && <div className="error-banner">{errorMessage(run.error)}</div>}
            {!result && !run.isError && (
              <div className="placeholder">
                {run.isPending ? 'Uploading files and running code in the sandbox...' : 'Run a task to see results here.'}
              </div>
            )}
            {result && (
              <>
                {result.files.length > 0 && (
                  <div className="field">
                    <label>Output files</label>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {result.files.map((f, i) => (
                        <button
                          key={`${f.fileName}-${i}`}
                          className="btn-secondary btn-sm"
                          onClick={() => downloadBase64(f.fileName, f.mediaType, f.data)}>
                          ⬇ {f.fileName}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {result.files.length === 0 && (
                  <div className="hint" style={{ marginBottom: 10 }}>
                    The task produced no files — the summary below explains what happened.
                  </div>
                )}
                <div className="completion-output bounded">{result.text}</div>
                <div className="usage-strip">
                  <span className="metric"><span className="metric-label">Model</span><span className="metric-value">{result.modelId}</span></span>
                  <span className="metric"><span className="metric-label">Tokens in</span><span className="metric-value">{fmtNumber(result.usage.inputTokens)}</span></span>
                  <span className="metric"><span className="metric-label">Tokens out</span><span className="metric-value">{fmtNumber(result.usage.outputTokens)}</span></span>
                  <span className="metric"><span className="metric-label">Cost</span><span className="metric-value">{fmtUsd(result.usage.estimatedCostUsd)}</span></span>
                  <span className="metric"><span className="metric-label">Latency</span><span className="metric-value">{fmtNumber(result.usage.latencyMs)} ms</span></span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
